import React, {Fragment, useContext, useEffect, useRef, useState} from "react";
import {GridColDef, GridFilterInputValueProps, GridFilterItem, GridFilterOperator} from "@mui/x-data-grid";
import {ATTEndpoints} from "../../../../services/att/endpoints";
import WebURL from "../../../../urls";
import CustomDataGrid, {DataGridRef} from "../../../../components/data-grid/CustomDataGrid";
import {useTranslation} from "react-i18next";
import {Box, Grid, TextField} from "@mui/material";
import {DatePicker} from "@mui/x-date-pickers";
import {useNavigate} from "react-router-dom";
import SnackbarUtil from "@/utils/SnackbarUtil";
import moment from "moment";
import TimecardService from "@/services/att/services/TimecardService";
import {DataGridContext, DataGridDispatchContext} from "@/components/data-grid/DataGridContext";
import ExportAction from "@/components/actions/Export";
import Util from "@/utils/Util";
import CustomBreadcrumbs from "@/components/breadcrumbs/CustomBreadcrumbs";
import AttendanceReportShare from "./AttendanceReportShare";
import CompanyService from "@/services/auth/CompanyService";
import ColumnSettingsDialog from "@/components/column-settings/index";
import {ColumnSettings, getColumnSettings, getDefaultColumnSettings} from "@/services/Template/TemplateService";
import GenericService from "@/services/GenericService";


const SUBMIT_FILTER_STROKE_TIME = 500;

interface InputNumberIntervalExtraProps {
  dateFormat: string;
}

type InputNumberIntervalProps = GridFilterInputValueProps & InputNumberIntervalExtraProps;


function InputNumberInterval(props: InputNumberIntervalProps) {
  const {t} = useTranslation();
  const {item, applyValue, dateFormat} = props;

  const filterTimeout = useRef<any>();
  const [filterValueState, setFilterValueState] = useState<[Date, Date]>([item.value[0], item.value[1]]);

  useEffect(() => {
    return () => {
      clearTimeout(filterTimeout.current);
    };
  }, []);

  const updateFilterValue = (lowerBound: Date, upperBounding: Date) => {
    clearTimeout(filterTimeout.current);
    setFilterValueState([lowerBound, upperBounding]);

    filterTimeout.current = setTimeout(() => {
      applyValue({...item, value: [lowerBound, upperBounding]});
    }, SUBMIT_FILTER_STROKE_TIME)
  };

  const handleUpperFilterChange = (event: any) => {
    const newUpperBound = event;
    updateFilterValue(filterValueState[0], newUpperBound);
  };
  const handleLowerFilterChange = (event: any) => {
    const newLowerBound = event;
    updateFilterValue(newLowerBound, filterValueState[1]);
  };

  return (
    <Box
      sx={{
        display: 'inline-flex',
        flexDirection: 'row',
        alignItems: 'end',
      }}
    >
      <DatePicker
        label={t("common.Start Date")}
        value={filterValueState[0] || new Date(new Date().getFullYear(), new Date().getMonth(), 1)}
        maxDate={filterValueState[1]}
        inputFormat={dateFormat}
        onChange={handleLowerFilterChange}
        renderInput={(params) =>
          <TextField
            variant="standard"
            {...params}
          />}
      />
      <DatePicker
        label={t("common.End Date")}
        value={filterValueState[1]}
        minDate={filterValueState[0]}
        inputFormat={dateFormat}
        onChange={handleUpperFilterChange}
        renderInput={(params) =>
          <TextField
            variant="standard"
            {...params}
          />}
      />
    </Box>
  )
}


const AttendanceReport: React.FC = () => {
  const attendanceReportShareRef = useRef<any>({})
  const {t, i18n} = useTranslation();
  const today = new Date()
  const firstDay = new Date()
  firstDay.setDate(1)
  const [exportFormat, setExportFormat] = useState<string>("");
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(0);
  const [start_date, setStartDate] = useState<Date>(firstDay);
  const [end_date, setEndDate] = useState<Date>(new Date());
  const [exportLoading, setExportLoading] = useState(false); // 下载 Loading
  const setFilter = (newValue: Record<any, any>) => {
    const {month} = newValue
    if (!month) {
      return
    }
    setFilterParams(newValue)
  }
  const [filterParams, setFilterParams] = useState<Record<any, any>>({
    date_range: [
      firstDay.toISOString().slice(0, 10),
      today.toISOString().slice(0, 10)
    ]
  });
  const [exportFilterParams, setExportFilterParams] = useState<Record<any, any>>({
    date_range: [
      start_date.toISOString().slice(0, 10),
      end_date.toISOString().slice(0, 10)
    ]
  });
  const [dateFormat, setDateFormat] = useState<string>("MM/dd/yyyy")

  const [gridColumn, setGridColumn] = useState<GridColDef[]>([]);
  const [columnReady, setColumnReady] = useState(false);

  useEffect(() => {
    CompanyService.dateFormat({method: 'get'}).then((res) => {
      const date_format = res.data?.date_format?.toString() || "MM/dd/yyyy";
      setDateFormat(date_format);
    });
  }, [])

  function isValidDate(date: any) {
    return date instanceof Date && !isNaN(date.getTime()) && date.getFullYear() > 2000
  }

  const WrappedInputNumberInterval = (props: GridFilterInputValueProps) => {
    return <InputNumberInterval {...props as InputNumberIntervalProps} dateFormat={dateFormat}/>;
  };

  const attDateOperators: GridFilterOperator[] = [
    {
      label: t("common.Between"),
      value: "between",
      getApplyFilterFn: (filterItem: GridFilterItem, column) => {
        if (!Array.isArray(filterItem.value) || filterItem.value.length !== 2) {
          return null;
        }
        return ({value}) => {
          return true;
        };
      },
      InputComponent: WrappedInputNumberInterval
    },
  ];

  useEffect(() => {
    dataGridRef.current?.refresh();
  }, [i18n.language])

  useEffect(() => {
    if (exportFormat) {
      setExportLoading(true);
      const export_meta = {
        fields: {
          employee_code: {alias: t("common.Person ID")},
          employee_name: {alias: t("common.Person Name")},
          timesheet_names: {alias: t("common.Timesheet")},
          att_dates: {alias: t("common.Date")},
          total_late_in: {alias: t("common.total_late_in")},
          total_early_out: {alias: t("common.total_early_out")},
          total_break: {alias: t("common.Total_break")},
          total_paid_work_deduct_break: {alias: t("common.Total_paid_work")},
          total_overtime_hrs: {alias: t("common.Total_overtime")},
          total_paid_work: {alias: t("common.Total_work")}
        },
        title: t("common.Attendance Report"),
        file_type: exportFormat,
        share: []
      }
      if (!start_date || !end_date || !isValidDate(start_date) || !isValidDate(end_date)) {
        SnackbarUtil.error("The start date and end date are required, Please select the correct start and end dates", {
          anchorOrigin: { vertical: 'top', horizontal: 'center' }
        })
        setExportLoading(false);
        setExportFormat('');
        return;
      }

      try {
        const data = export_meta;
        let params = '';

        setFilterParams({
          "all": true,
          date_range: [
            Util.datetime2string(start_date, "yyyy-MM-dd"),
            Util.datetime2string(end_date, "yyyy-MM-dd")
          ]
        })

        params = `current=${page + 1}&pageSize=${pageSize}&date_range=${moment(start_date).format(
          'yyyy-MM-DD')}&date_range=${moment(end_date).format(
          'yyyy-MM-DD')}&keyword=${searchKeyword}&all=${true}`;

        TimecardService.download_data(params, data, "attendance")
          .then((response) => {
            if (!response || !response.data) {
              throw new Error("Empty file response");
            }

            const contentType = response.headers?.["content-type"] || "application/octet-stream";
            const blob = new Blob([response.data], { type: contentType });

            // 异步执行，防止大数据阻塞UI
            setTimeout(() => {
              const url = URL.createObjectURL(blob);
              const aLink = document.createElement("a");
              aLink.style.display = "none";
              aLink.href = url;

              // 从header获取文件名
              const disposition = response.headers?.["content-disposition"];
              if (disposition) {
                const filenameMatch = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (filenameMatch != null && filenameMatch[1]) {
                  aLink.download = filenameMatch[1].replace(/['"]/g, "");
                }
              }

              document.body.appendChild(aLink);
              aLink.click();

              // 延迟释放，避免大文件被提前销毁
              setTimeout(() => {
                document.body.removeChild(aLink);
                URL.revokeObjectURL(url);
              }, 100);
            }, 0);

            SnackbarUtil.success(t("common.success"));
          })
          .catch((e) => {
            console.error("Download failed", e);
            SnackbarUtil.error(t("common.Download") + t("common.failure"), {
              anchorOrigin: {
                vertical: "top",
                horizontal: "center"
              }
            });
          })
          .finally(() => {
            setExportLoading(false);
            setExportFormat('');
          });

      } catch (e) {
        setExportLoading(false);
        setExportFormat('');
      }
    }
  }, [exportFormat, page, pageSize, searchKeyword, start_date, end_date, t]);

  const defaultColumns: GridColDef[] = [
    {headerName: t("common.Person ID"), field: 'employee_code', width: 120, filterable: false},
    {headerName: t("common.Person Name"), field: 'employee_name', width: 160, filterable: false},
    {headerName: t("common.Timesheet"), field: 'timesheet_names', width: 120, filterable: false},
    {headerName: t("common.Date"), field: 'att_dates', width: 120, type: "date", filterOperators: attDateOperators,},
    {headerName: t("common.total_late_in"), field: 'total_late_in', width: 140, filterable: false},
    {headerName: t("common.total_early_out"), field: 'total_early_out', width: 150, filterable: false},
    {headerName: t("common.Total_break"), field: 'total_break', width: 140, filterable: false},
    {headerName: t("common.Total_paid_work"), field: 'total_paid_work_deduct_break', width: 140, filterable: false},
    {headerName: t("common.Total_overtime"), field: 'total_overtime_hrs', width: 140, filterable: false},
    {headerName: t("common.Total_work"), field: 'total_paid_work', width: 140, filterable: false},
  ];

  const safeParse = (data: any): any[] => {
    if (Array.isArray(data)) return data;
    if (typeof data === 'string') {
      try {
        return JSON.parse(data);
      } catch (e) {
        return [];
      }
    }
    return [];
  };

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getColumnSettings("attendance");
        let list: ColumnSettings[] = res.data?.data || [];

        if (list.length === 0) {
          const def = await getDefaultColumnSettings("attendance");
          list = def as ColumnSettings[];
        }
        const active = list.find(x => x.is_active) || list[0];
        if (active) {
          const cfg = safeParse(active.template_field);
          const sortedConfig = [...cfg].sort((a, b) => (a.sort || 0) - (b.sort || 0));

          const final = sortedConfig
            .filter((x: any) => x.isCheck)
            .map((x: any) => defaultColumns.find(c => c.field === x.key))
            .filter((item): item is GridColDef => !!item);

          setGridColumn(final.length ? final : defaultColumns);
        }
      } catch (e) {
        setGridColumn(defaultColumns);
      } finally {
        setColumnReady(true);
      }
    };
    load();
  }, [t]);

  const handleColumnsChange = (cfg: any[]) => {
    if (!cfg) return;
    const sortedConfig = [...cfg].sort((a, b) => (a.sort || 0) - (b.sort || 0));

    const finalColumns = sortedConfig
      .filter((item) => item.isCheck)
      .map((item) => defaultColumns.find((col) => col.field === item.key))
      .filter((item): item is GridColDef => !!item);

    setGridColumn(finalColumns.length ? finalColumns : defaultColumns);
    setTimeout(() => {
      dataGridRef.current?.refresh();
    }, 80);
  };

  const navigate = useNavigate();
  const dataGridRef = useRef<DataGridRef>();
  const ExtraAction = (props: any) => {
    const dispatch = useContext(DataGridDispatchContext)
    const {page} = useContext(DataGridContext)
    const {pageSize} = useContext(DataGridContext)
    const {searchKeyword} = useContext(DataGridContext)

    useEffect(() => {
      setPage(page)
      setPageSize(pageSize)
      setSearchKeyword(searchKeyword)
    }, [page, pageSize, searchKeyword])

    const handleRefresh = () => {
      dispatch({type: "refreshed"})
    }

    return (
      <Fragment>
        <Grid item={true}>
          <Box>
            <Grid
              container={true}
              sx={{
                alignItems: "center",
                marginLeft: "5px"
              }}
            >
              <Box sx={{width: "150px"}}>
                <DatePicker
                  disableFuture
                  views={['year', 'month', 'day']}
                  value={start_date}
                  maxDate={end_date}
                  inputFormat={dateFormat}
                  onChange={(newValue: any) => {
                    if (!newValue) {
                      SnackbarUtil.error(t('export.Start date cannot be null.'), {
                        anchorOrigin: {vertical: 'top', horizontal: 'center'}
                      })
                      return
                    }
                    setStartDate(newValue);
                    setFilterParams({
                      date_range: [
                        Util.datetime2string(newValue, "yyyy-MM-dd"),
                        Util.datetime2string(end_date, "yyyy-MM-dd")
                      ]
                    })
                    handleRefresh()
                  }}
                  renderInput={(params) =>
                    <TextField
                      size={"small"}
                      variant="outlined"
                      {...params}
                      inputProps={{...params.inputProps, readOnly: true}}
                    />}
                />
              </Box>
              -
              <Box sx={{width: "150px"}}>
                <DatePicker
                  disableFuture
                  views={['year', 'month', 'day']}
                  value={end_date}
                  minDate={start_date}
                  inputFormat={dateFormat}
                  onChange={(newValue: any) => {
                    if (!newValue) {
                      SnackbarUtil.error(t('export.End date cannot be null.'), {
                        anchorOrigin: {vertical: 'top', horizontal: 'center'}
                      })
                      return
                    }
                    setEndDate(newValue)
                    setFilterParams({
                      date_range: [
                        Util.datetime2string(start_date, "yyyy-MM-dd"),
                        Util.datetime2string(newValue, "yyyy-MM-dd")
                      ]
                    })
                    handleRefresh()
                  }}
                  renderInput={(params) =>
                    <TextField
                      size={"small"}
                      variant="outlined"
                      {...params}
                      inputProps={{...params.inputProps, readOnly: true}}
                    />}
                />
              </Box>
            </Grid>
          </Box>
        </Grid>
      </Fragment>
    )
  }

  const toolbar = {
    title: t("common.First In Last Out"),
    breadcrumbs: [
      {breadcrumbName: t("common.Report"), path: WebURL.ATT_REPORT_HOME},
      {breadcrumbName: t("common.Attendance Report"), path: WebURL.ATT_REPORT_MONTHLY_REPORT},
    ],
    searchTips: t("common.Search by Person ID, Person Name, Timesheet Name"),
    search: true,
    children: <ExtraAction filterParams={exportFilterParams} setFilterParams={setFilter}
                           setExportFormat={setExportFormat}/>,
    actions: [
      {
        key: 'export', iconFont: 'icon-ic_export', helpTxt: t("export.Export"),
        callback: async () => {
          attendanceReportShareRef?.current?.showReport()
        }
      }
    ],
    extraComponentOnRight:
      <Grid sx={{marginRight: '-15px'}}>
        <ColumnSettingsDialog
          report_type="attendance"
          onColumnsChange={handleColumnsChange}
        />
        <ExportAction
          setExportFormat={(fileType: string) => {
            setExportFormat(fileType);
          }}
          loading={exportLoading}
        />
      </Grid>
  }

  return (
    <>
      <Box
        sx={{
          '& .MuiDataGrid-row-height-no-limit': {
            maxHeight: '180px!important'
          },
        }}
      >
        <CustomBreadcrumbs record={[{label: t("common.Attendance Report")}]}/>

        {columnReady && (
          <CustomDataGrid
            columns={gridColumn}
            ref={dataGridRef}
            uri={ATTEndpoints.ATT_REPORT_ATTENDANCE_REPORT}
            requestParams={filterParams}
            getRowId={() => Math.random()}
            toolbar={toolbar}
            localeText={{
              filterPanelColumns: t("common.Columns"),
              filterPanelOperators: t("common.Operators"),
            }}
          />
        )}
      </Box>
      <AttendanceReportShare date_format={dateFormat} ref={attendanceReportShareRef}></AttendanceReportShare>
    </>
  )
}

export default AttendanceReport
